import gsap from 'gsap';
import { MotionPathPlugin, ScrollTrigger } from 'gsap/all';

gsap.registerPlugin(MotionPathPlugin);
gsap.registerPlugin(ScrollTrigger);

// Global Funcs
const random = (min, max) => Math.random() * (max - min) + min;
const createCirclePath = (size) => {
  const radius = size / 2;
  return [
    { x: 0, y: 0 },
    { x: radius, y: -radius },
    { x: 0, y: -size },
    { x: -radius, y: -radius },
    { x: 0, y: 0 },
  ];
};

// Element Funcs
const animateBubble = (bubble) => {
  const circleSize = random(20, 100);
  const direction = Math.random() > 0.5 ? 1 : -1;
  const duration = random(20, 50);
  const scale = random(0.5, 1);

  gsap.to(bubble, {
    motionPath: {
      path: createCirclePath(circleSize),
      curviness: 1.5,
    },
    rotation: 360 * direction,
    duration,
    scale,
    repeat: -1,
    ease: 'power1.inOut',
    yoyo: true,
  });
};

const animateButterfly = (bubble) => {
  const circleSize = random(10, 15);
  const duration = random(20, 50);
  const scale = random(0.5, 1);

  gsap.to(bubble, {
    motionPath: {
      path: createCirclePath(circleSize),
      curviness: 1.5,
    },
    rotation: 10,
    duration,
    scale,
    repeat: -1,
    ease: 'power1.inOut',
    yoyo: true,
  });
};

const animateClouds = (cloud) => {
  const duration = random(30, 60);
  const scale = random(0.5, 1);
  const translateX = random(-20, 20);

  gsap.to(cloud, {
    opacity: 0.5,
    translateX: `${translateX}%`,
    duration,
    scale,
    repeat: -1,
    ease: 'power1.inOut',
    yoyo: true,
  });
};

const animatePlankton = (element) => {
  const tl = gsap.timeline();

  tl.to(element, {
    rotation: 20,
    duration: random(5, 10),
    repeat: -1,
    yoyo: true,
    ease: 'power1.inOut',
  }).to(
    element,
    {
      y: random(10, 20),
      duration: random(2, 4),
      repeat: -1,
      yoyo: true,
      ease: 'sine.inOut',
    },
    0,
  );
};

const animateJellyfish = (element) => {
  const circleSize = random(30, 50);

  gsap.to(element, {
    motionPath: {
      path: createCirclePath(circleSize),
      curviness: 1.8,
    },
    duration: random(15, 20),
    repeat: -1,
    ease: 'sine.inOut',
  });
};

const animateBoat = (element) => {
  const circleSize = random(5, 10);

  gsap.to(element, {
    motionPath: {
      path: createCirclePath(circleSize),
      curviness: 1.8,
    },
    rotate: -10,
    duration: random(15, 20),
    repeat: -1,
    yoyo: true,
    ease: 'sine.inOut',
  });
};

const animateMonkey = (element) => {
  gsap.to(element, {
    rotate: -8,
    duration: 3,
    transformOrigin: 'top center',
    repeat: -1,
    yoyo: true,
    ease: 'sine.inOut',
  });
};

document.addEventListener('DOMContentLoaded', () => {
  const planktons = Array.from(document.querySelectorAll('.js-animation-plankton'));
  const jellyfishs = Array.from(document.querySelectorAll('.js-animation-jellyfish'));
  const boats = Array.from(document.querySelectorAll('.js-animation-boat'));
  const bubbles = Array.from(document.querySelectorAll('.js-animation-bubble'));
  const clouds = Array.from(document.querySelectorAll('.js-animation-cloud'));
  const monkeys = Array.from(document.querySelectorAll('.js-animation-monkey'));
  const butterflies = Array.from(document.querySelectorAll('.js-animation-butterfly'));

  bubbles.forEach((bubble) => bubble && animateBubble(bubble));
  clouds.forEach((bubble) => bubble && animateClouds(bubble));
  planktons.forEach((plankton) => plankton && animatePlankton(plankton));
  jellyfishs.forEach((jellyfish) => jellyfish && animateJellyfish(jellyfish));
  boats.forEach((boat) => boat && animateBoat(boat));
  monkeys.forEach((monkey) => monkey && animateMonkey(monkey));
  butterflies.forEach((butterfly) => butterfly && animateButterfly(butterfly));

  const nickLandSections = Array.from(document.querySelectorAll('.js-animation-nick'));
  nickLandSections.forEach((container) => {
    gsap
      .timeline({
        scrollTrigger: {
          trigger: container,
          start: '50% bottom',
          markers: false,
        },
      })
      .from('.js-animation-spaceship', {
        opacity: 0,
        y: 50,
        transformOrigin: 'bottom right',
        duration: 2,
      })
      .from(
        '.js-animation-sky',
        {
          scale: 0,
          x: 50,
          rotate: -25,
          transformOrigin: 'bottom right',
          duration: 1,
          ease: 'power4.inOut',
        },
        '-=1',
      )
      .from(
        '.js-animation-truck',
        {
          scale: 0,
          x: 10,
          y: 50,
          transformOrigin: 'center left',
          duration: 1,
          ease: 'power4.inOut',
        },
        '-=0.5',
      )
      .from(
        '.js-animation-bob-patrick',
        {
          scale: 0,
          transformOrigin: 'bottom center',
          duration: 1,
          ease: 'power4.inOut',
        },
        '-=0.3',
      )
      .from('.js-animation-nick-logo', {
        opacity: 0,
        scale: 2,
        duration: 1,
        ease: 'bounce.out',
      })
      .from('.js-animation-nick-shine', {
        opacity: 0,
        scale: 0,
        duration: 1,
      })
      .to('.js-animation-sky', {
        y: 20,
        repeat: -1,
        yoyo: true,
        duration: 4,
        ease: 'power1.inOut',
      })
      .to(
        '.js-animation-nick-shine',
        {
          rotate: 360,
          duration: 20,
          repeat: -1,
          ease: 'none',
        },
        '<',
      )
      .to(
        '.js-animation-nick-shine',
        {
          scale: 1.2,
          duration: 2,
          repeat: -1,
          yoyo: true,
          ease: 'power1.inOut',
        },
        '<',
      );
  });

  const mashaBearSummerSections = Array.from(
    document.querySelectorAll('.js-animation-masha-bear-summer'),
  );

  mashaBearSummerSections.forEach((container) => {
    gsap
      .timeline({
        scrollTrigger: {
          trigger: container,
          start: 'top center',
          end: '+=25% center',
          scrub: 1,
          markers: false,
        },
      })
      .set('.js-animation-wolf', {
        zIndex: -1,
      })
      .from('.js-animation-wolf', {
        x: '100%',
      })
      .set('.js-animation-wolf', {
        zIndex: 0,
      });
  });
});
